import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { WarEvent, AIAlert, TeamPresencePayload, AIAlertPayload } from '../types/event.types'
import type { TeamPresence } from '../types/session.types'
import { WarRoomWSClient, type WSStatus } from '../api/wsClient'

// ── Types ──────────────────────────────────────────────────────────────────

export interface TeamPresenceState {
  teamId: string
  teamSlug: string
  onlineUserIds: string[]
  status: TeamPresence
}

interface WarRoomState {
  // Connection
  sessionId: string | null
  wsStatus: WSStatus
  wsClient: WarRoomWSClient | null

  // Presence — all teams in session
  teamPresence: Record<string, TeamPresenceState>

  // AI Alerts
  alerts: AIAlert[]
  unreadAlertCount: number

  // Recent events (for Live Feed — last 50)
  recentEvents: Array<{ id: string; event: WarEvent; receivedAt: Date }>

  // Actions
  connect: (sessionId: string, teamId: string | undefined, token: string) => void
  disconnect: () => void
  dismissAlert: (alertId: string) => void
  dismissAllAlerts: () => void
  clearEvents: () => void
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useWarRoomStore = create<WarRoomState>()(
  subscribeWithSelector((set, get) => ({
    sessionId: null,
    wsStatus: 'disconnected',
    wsClient: null,
    teamPresence: {},
    alerts: [],
    unreadAlertCount: 0,
    recentEvents: [],

    connect: (sessionId, teamId, token) => {
      // 1. Cleanup existing connection
      get().disconnect()

      // 2. Create new WS client
      const client = new WarRoomWSClient({
        sessionId,
        teamId,
        token,

        onStatusChange: (status) => set({ wsStatus: status }),

        onEvent: (evt: WarEvent) => {
          // Append to Live Feed (cap at 50, newest first)
          set((state) => ({
            recentEvents: [
              { id: crypto.randomUUID(), event: evt, receivedAt: new Date() },
              ...state.recentEvents,
            ].slice(0, 50),
          }))

          // Handle specific event types
          handleWarEvent(evt, set)
        },

        onError: (err) => {
          console.warn('[WarRoom WS] connection issue', err)
        },
      })

      set({ sessionId, wsClient: client, wsStatus: 'connecting' })
      client.connect()
    },

    disconnect: () => {
      const { wsClient } = get()
      wsClient?.disconnect()
      set({
        sessionId: null,
        wsStatus: 'disconnected',
        wsClient: null,
        teamPresence: {},
        alerts: [],
        unreadAlertCount: 0,
        recentEvents: [],
      })
    },

    dismissAlert: (alertId) =>
      set((state) => ({
        alerts: state.alerts.map((a) =>
          a.id === alertId ? { ...a, dismissed: true } : a
        ),
        unreadAlertCount: Math.max(0, state.unreadAlertCount - 1),
      })),

    dismissAllAlerts: () =>
      set((state) => ({
        alerts: state.alerts.map((a) => ({ ...a, dismissed: true })),
        unreadAlertCount: 0,
      })),

    clearEvents: () => set({ recentEvents: [] }),
  }))
)

// ── Event Handler (outside store to keep store lean) ───────────────────────

export function handleWarEvent(
  evt: WarEvent,
  set: (fn: (state: WarRoomState) => Partial<WarRoomState>) => void
): void {
  switch (evt.event_type) {
    case 'team.online':
    case 'team.joined': {
      const p = evt.payload as TeamPresencePayload
      set((state) => ({
        teamPresence: {
          ...state.teamPresence,
          [p.team_id]: {
            ...(state.teamPresence[p.team_id] ?? {
              teamId: p.team_id,
              teamSlug: p.team_slug,
              onlineUserIds: [],
            }),
            status: 'online',
            onlineUserIds: [
              ...new Set([
                ...(state.teamPresence[p.team_id]?.onlineUserIds ?? []),
                p.user_id,
              ]),
            ],
          },
        },
      }))
      break
    }

    case 'team.offline': {
      const p = evt.payload as TeamPresencePayload
      set((state) => {
        const current = state.teamPresence[p.team_id]
        if (!current) return state
        const onlineUserIds = current.onlineUserIds.filter((id) => id !== p.user_id)
        return {
          teamPresence: {
            ...state.teamPresence,
            [p.team_id]: {
              ...current,
              status: onlineUserIds.length === 0 ? 'offline' : 'online',
              onlineUserIds,
            },
          },
        }
      })
      break
    }

    case 'ai.alert': {
      const p = evt.payload as AIAlertPayload
      const alert: AIAlert = {
        id: crypto.randomUUID(),
        session_id: evt.session_id,
        event_type: evt.event_type,
        received_at: new Date(),
        alert_type: p.alert_type,
        severity: p.severity,
        message: p.message,
        source: p.source,
        step_id: p.step_id,
        team_id: p.team_id,
        action_url: p.action_url,
        dismissed: false,
      }
      set((state) => ({
        alerts: [alert, ...state.alerts].slice(0, 100), // cap at 100
        unreadAlertCount: state.unreadAlertCount + 1,
      }))
      break
    }

    default:
      break
  }
}
